import { z } from 'zod';
import type { IsoDate } from './common.js';
import type { UserSummary } from './users.js';

export const meetingResponses = ['pending', 'yes', 'maybe', 'no'] as const;
export type MeetingResponse = (typeof meetingResponses)[number];

export const meetingResponseLabels: Record<MeetingResponse, string> = {
  pending: 'Sin responder',
  yes: 'Asistiré',
  maybe: 'Quizá',
  no: 'No asistiré',
};

export const createMeetingSchema = z.object({
  title: z.string().trim().min(1, 'Obligatorio').max(120),
  description: z.string().trim().max(2000).optional(),
  /** Conversation whose call hosts the meeting (usually a voice channel). */
  channelId: z.uuid(),
  startsAt: z.iso.datetime(),
  durationMinutes: z.number().int().min(5).max(480).default(30),
  attendeeIds: z.array(z.uuid()).max(100).default([]),
  /** Create a public link so people without an account can join. */
  guestLink: z.boolean().default(false),
});
export type CreateMeetingInput = z.input<typeof createMeetingSchema>;

export const updateMeetingSchema = createMeetingSchema.omit({ channelId: true }).partial();
export type UpdateMeetingInput = z.input<typeof updateMeetingSchema>;

export const respondMeetingSchema = z.object({ response: z.enum(['yes', 'maybe', 'no']) });
export type RespondMeetingInput = z.infer<typeof respondMeetingSchema>;

export const guestJoinSchema = z.object({ name: z.string().trim().min(1).max(60) });
export type GuestJoinInput = z.infer<typeof guestJoinSchema>;

export interface MeetingAttendee extends UserSummary {
  response: MeetingResponse;
}

export interface Meeting {
  id: string;
  teamId: string;
  channelId: string;
  title: string;
  description: string | null;
  startsAt: IsoDate;
  endsAt: IsoDate;
  durationMinutes: number;
  createdBy: UserSummary;
  attendees: MeetingAttendee[];
  /** Public join URL for guests (null when disabled or for non-organisers). */
  guestUrl: string | null;
  cancelledAt: IsoDate | null;
  createdAt: IsoDate;
}

/** What a guest sees before joining (no private data). */
export interface MeetingGuestInfo {
  title: string;
  teamName: string;
  organizer: string;
  startsAt: IsoDate;
  endsAt: IsoDate;
  /** Guests can join from 15 min before the start until 1 h after the end. */
  open: boolean;
  cancelled: boolean;
}

/** Window in which a meeting accepts guests. */
export function guestWindow(startsAt: Date, durationMinutes: number) {
  return {
    opensAt: new Date(startsAt.getTime() - 15 * 60_000),
    closesAt: new Date(startsAt.getTime() + (durationMinutes + 60) * 60_000),
  };
}

/** Private link to subscribe to your Nexo calendar from Google, Outlook or Apple Calendar. */
export interface CalendarFeed {
  url: string;
}
